import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { styles, SdiHeart, DataCard, Loader, AlertSingleLine } from '@sdir/sds';
import styled from 'styled-components';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { HealthLimitation } from '@sdir/blueprint.aps/lib/components';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { InfoPageLayout } from '../../Organisms';
import { getHealthStatusApi } from '../../../httpclient';
import { useCurrentPerson, useFeatureToggles } from '../../../services/hooks';
import { ToggleHealth } from '../../../constants/permissions';

const HealthPage: React.FC = () => {
  const intl = useIntl();
  const featureToggle = useFeatureToggles();

  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showHealth = !isToggleOn(featureToggle?.result, ToggleHealth, userPersonalNr);

  const { result, loading, error } = useApi({
    apiFunction: () => getHealthStatusApi.v1HealthcheckGetownhealthcheckGet(),
    callApiOnInit: true,
  });

  const healthData = useMemo(() => {
    return {
      ...result,
      lastColorSightTestDate: result?.lastColorSightTestDate || undefined,
      limitedDescription: result?.limitedDescription || undefined,
      serviceLimitation: result?.serviceLimitation || undefined,
      tradeAreaLimitation: result?.tradeAreaLimitation || undefined,
    };
  }, [result]);

  if (!showHealth) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  return (
    <InfoPageLayout
      title={intl.formatMessage({ id: 'home.tile.health' })}
      description={intl.formatMessage({ id: 'health.header.description' }, { br: () => <br /> })}
      icon={<SdiHeart color={styles.colors.secondary.seaBlue4} size="l" />}
      extraInfoTitle={intl.formatMessage({ id: 'health.subheader.sideinfobox' })}
      extraInfoDescription={intl.formatMessage(
        { id: 'health.description.sideinfobox' },
        { br: () => <br /> }
      )}
    >
      <DataCard title={intl.formatMessage({ id: 'home.tile.health' })}>
        {loading ? (
          <Loader />
        ) : result ? (
          <HealthSubDetails>
            <div>
              <Label>{intl.formatMessage({ id: 'sailor' })}</Label>
              <HealthLimitation healthCheck={healthData} />
            </div>
          </HealthSubDetails>
        ) : (
          <EmptyResponse>
            {intl.formatMessage({ id: error !== '' ? 'health.error' : 'health.no.found' })}
          </EmptyResponse>
        )}
      </DataCard>
    </InfoPageLayout>
  );
};

export default HealthPage;

const EmptyResponse = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3em;
  margin-top: 3em;
`;

const HealthSubDetails = styled.div`
  margin: 3rem;

  & > div {
    margin-bottom: 10px;
  }
`;

const Label = styled.div`
  color: ${styles.colors.font.text50};
  font-family: ${styles.fonts.stfMedium};
  margin-bottom: 0.8rem;
`;
